/**
 * Page integrity: discover internal nav links in layout_config, ensure matching `pages[pageKey]` exist.
 * Non-destructive: never removes or overwrites existing pages (only creates missing keys).
 */
import { classifySectionButtonLink, sanitizeSectionButtonHref } from "@/lib/sections/sectionButtonLink";
import { v4 as uuidv4 } from "uuid";

// ── Legacy type stubs (kept for type compatibility; no runtime dependency) ──
type StoreSection = Record<string, unknown>;
type TemplateConfig = Record<string, unknown>;

const LINKISH_KEYS = new Set([
    "link",
    "href",
    "url",
    "buttonLink",
    "ctaLink",
    "to",
]);

/** Path segment aliases → canonical page keys used in `layout_config.pages` and storefront routes. */
const SEGMENT_TO_PAGE_KEY: Record<string, string> = {
    "": "home",
    product: "product",
    category: "category",
    collection: "category",
    cart: "cart",
    checkout: "checkout",
    account: "account",
    search: "search",
    shop: "shop",
    promo: "promo",
    contact: "contact",
    about: "about",
    faq: "faq",
    tracking: "tracking",
    policy: "policy",
    privacy: "policy",
    terms: "policy",
    cgv: "policy",
    blog: "blog",
    wishlist: "wishlist",
    login: "login",
    "order-confirmation": "order-confirmation",
};

/**
 * System pages that must not stay blank: missing key or `[]` gets default sections (heal).
 * Aligns with storefront routes (privacy/terms → policy).
 */
export const SYSTEM_PAGE_KEYS_FOR_EMPTY_HEAL = new Set([
    "home",
    "shop",
    "about",
    "contact",
    "policy",
]);

export type PageIntegrityResult = {
    /** Mutated layout root (same reference as input). */
    config: Record<string, unknown>;
    /** Page keys that were created. */
    healedKeys: string[];
    /** Dev-oriented messages (also logged as warnings). */
    warnings: string[];
};

function stripQueryHash(s: string): string {
    const noHash = s.split("#")[0] ?? "";
    return (noHash.split("?")[0] ?? "").trim();
}

/**
 * Strip optional `/store/:slug` prefix so `/store/my-shop/about` → `/about`.
 */
export function stripStoreSlugPrefix(path: string): string {
    const p = stripQueryHash(path);
    const m = p.match(/^\/store\/[^/]+(\/.*)?$/i);
    if (m) {
        const rest = m[1];
        if (!rest || rest === "") return "/";
        return rest.startsWith("/") ? rest : `/${rest}`;
    }
    return p.startsWith("/") ? p : `/${p}`;
}

/**
 * Map an internal path to a `pages` key. Returns null if not an internal route bucket (e.g. external only).
 */
export function internalPathToPageKey(path: string): string | null {
    const normalized = stripStoreSlugPrefix(path);
    const cls = classifySectionButtonLink(normalizeLinkForClassify(normalized));
    if (cls.kind !== "internal_path") return null;

    const noHash = stripQueryHash(normalized);
    const pathOnly = noHash.startsWith("/") ? noHash : `/${noHash}`;
    if (pathOnly === "/" || pathOnly === "") return "home";

    const parts = pathOnly.split("/").filter(Boolean);
    if (parts.length === 0) return "home";

    const first = parts[0].toLowerCase();
    return SEGMENT_TO_PAGE_KEY[first] ?? first;
}

function normalizeLinkForClassify(raw: string): string {
    const t = stripQueryHash(raw).trim();
    if (!t) return "#";
    if (t.startsWith("/") || t.startsWith("#")) return t;
    if (!t.includes(":") && !/\s/.test(t) && /^[a-zA-Z0-9/_-]+$/.test(t)) return `/${t}`;
    return t;
}

function maybeCollectLinkString(raw: unknown, into: Set<string>): void {
    if (typeof raw !== "string") return;
    const s = raw.trim();
    if (!s) return;
    const cls = classifySectionButtonLink(sanitizeSectionButtonHref(normalizeLinkForClassify(s)));
    if (cls.kind !== "internal_path") return;
    into.add(s);
}

function walkForLinkValues(node: unknown, into: Set<string>, depth: number): void {
    if (depth > 12 || node == null) return;
    if (typeof node === "string") return;
    if (Array.isArray(node)) {
        for (const item of node) walkForLinkValues(item, into, depth + 1);
        return;
    }
    if (typeof node !== "object") return;
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
        const keyLower = k.toLowerCase();
        if (LINKISH_KEYS.has(k) || keyLower.endsWith("link")) {
            maybeCollectLinkString(v, into);
        }
        if (v && typeof v === "object") walkForLinkValues(v, into, depth + 1);
    }
}

/**
 * Collect internal path strings from header, footer, and all page sections.
 */
export function collectInternalLinkStringsFromLayout(layout: Record<string, unknown>): Set<string> {
    const into = new Set<string>();
    const header = layout.header as { props?: Record<string, unknown> } | undefined;
    const footer = layout.footer as { props?: Record<string, unknown> } | undefined;
    if (header?.props) walkForLinkValues(header.props, into, 0);
    if (footer?.props) walkForLinkValues(footer.props, into, 0);

    const pages = layout.pages as Record<string, unknown> | undefined;
    if (pages && typeof pages === "object") {
        for (const sections of Object.values(pages)) {
            if (!Array.isArray(sections)) continue;
            for (const sec of sections) {
                if (sec && typeof sec === "object") {
                    walkForLinkValues((sec as StoreSection).props, into, 0);
                    walkForLinkValues((sec as StoreSection).blocks, into, 0);
                }
            }
        }
    }
    return into;
}

export function collectReferencedPageKeysFromLayout(layout: Record<string, unknown>): Set<string> {
    const keys = new Set<string>();
    for (const link of collectInternalLinkStringsFromLayout(layout)) {
        const pk = internalPathToPageKey(link);
        if (pk) keys.add(pk);
    }
    return keys;
}

function markAutoGenerated(sections: StoreSection[]): StoreSection[] {
    return sections.map((s) => ({ ...s, isAutoGenerated: true }));
}

/**
 * Build a minimal placeholder section for a page key.
 */
export function generateDefaultPageSections(
    pageKey: string,
    _templateShell?: TemplateConfig
): StoreSection[] {
    return markAutoGenerated([
        {
            id: uuidv4(),
            type: "rich-text",
            visible: true,
            props: {
                title: pageKey,
                content: `<p>Page "${pageKey}" generated automatically.</p>`,
            },
        } as StoreSection,
    ]);
}

/**
 * Ensure `layout.pages` contains every page key referenced by internal links.
 * Mutates `layout` in place. Never deletes or replaces existing page arrays.
 */
export function healLayoutPageIntegrity(
    layout: Record<string, unknown>,
    ctx?: { templateId?: number; logger?: (msg: string) => void }
): PageIntegrityResult {
    const warnings: string[] = [];
    const healedKeys: string[] = [];

    if (!layout.pages || typeof layout.pages !== "object") {
        layout.pages = {};
    }
    const pages = layout.pages as Record<string, StoreSection[]>;
    const referenced = collectReferencedPageKeysFromLayout(layout);

    for (const pageKey of referenced) {
        if (pageKey in pages && pages[pageKey] !== undefined) continue;

        try {
            pages[pageKey] = generateDefaultPageSections(pageKey);
            healedKeys.push(pageKey);
            const msg = `[PageIntegrity] Auto-created missing page "${pageKey}" (non-destructive).`;
            warnings.push(msg);
            ctx?.logger?.(msg);
            if (import.meta.env.DEV) console.warn(msg);
        } catch (e) {
            const msg = `[PageIntegrity] Failed to generate page "${pageKey}": ${e instanceof Error ? e.message : String(e)}`;
            warnings.push(msg);
            ctx?.logger?.(msg);
            console.warn(msg);
        }
    }

    return { config: layout, healedKeys, warnings };
}

/**
 * Return a safe internal path for navigation. External / tel / mailto / hash links are returned unchanged.
 * If the target page key is missing from `layout.pages`, returns `/` (or store-prefixed equivalent via caller).
 */
export function resolveInternalLink(
    link: string,
    layout?: { pages?: Record<string, unknown> }
): string {
    const raw = sanitizeSectionButtonHref(link);
    const cls = classifySectionButtonLink(raw);
    if (cls.kind === "internal_hash") return raw.startsWith("#") ? raw : `#${raw}`;
    if (cls.kind !== "internal_path") return raw;

    const normalizedInput = normalizeLinkForClassify(raw);
    const pageKey = internalPathToPageKey(normalizedInput);
    if (!pageKey) return "/";

    const pages = layout?.pages;
    if (pages && typeof pages === "object") {
        if (!(pageKey in pages)) {
            if (import.meta.env.DEV) {
                console.warn(`[resolveInternalLink] Missing page "${pageKey}" — falling back to "/"`);
            }
            return "/";
        }
        const row = pages[pageKey];
        if (
            SYSTEM_PAGE_KEYS_FOR_EMPTY_HEAL.has(pageKey) &&
            (!Array.isArray(row) || row.length === 0)
        ) {
            if (import.meta.env.DEV) {
                console.warn(`[resolveInternalLink] Empty system page "${pageKey}" — falling back to "/"`);
            }
            return "/";
        }
    }

    const path = normalizedInput.startsWith("/") ? normalizedInput : `/${normalizedInput}`;
    return path;
}

export function getExistingPageKeys(layout: Record<string, unknown>): Set<string> {
    const pages = layout.pages as Record<string, unknown> | undefined;
    if (!pages || typeof pages !== "object") return new Set();
    return new Set(Object.keys(pages));
}
